/**
 * Modal "Registrar pago manual" (Sprint 21, S21-C / S21-B1).
 *
 * El tesorero registra a mano un pago de cuota cuyo aviso del banco no llegó
 * por correo (le avisan por WhatsApp con la foto del comprobante). Captura lo
 * que se lee del comprobante; el backend (POST /pagos/manual) autollena los
 * campos internos del modelo. El pago entra en `por_distribuir` y luego se
 * atribuye a la casa/cuota con el botón "Clasificar →" (flujo existente).
 */
import { useState } from 'react';
import { ApiError } from '../../lib/api/client';
import { registrarPagoManual } from '../../lib/api/pagos';
import {
  BANCOS_CODIGOS,
  BANCOS_LABEL,
  type BancoCodigo,
  type PagoRead,
} from '../../lib/api/types';
import { hoy } from '../../lib/tiempo';
import { InputFecha } from '../ui/InputFecha';

export interface ModalRegistrarPagoManualProps {
  onClose: () => void;
  onSuccess: (pago: PagoRead) => void;
}

/** Extrae un mensaje legible del error de la API (detail string, detail array
 * de Pydantic, o fallback por status). */
function mensajeError(e: unknown): string {
  if (e instanceof ApiError) {
    const detail = (e.body as { detail?: unknown } | null)?.detail;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail) && detail.length > 0) {
      const msg = (detail[0] as { msg?: string })?.msg;
      if (msg) return msg;
    }
    if (e.status === 409) {
      return 'Ya existe un pago con esa operación, fecha y monto.';
    }
    return `Error ${e.status} al registrar el pago.`;
  }
  return e instanceof Error ? e.message : 'Error inesperado.';
}

export function ModalRegistrarPagoManual({
  onClose,
  onSuccess,
}: ModalRegistrarPagoManualProps) {
  const [monto, setMonto] = useState('');
  const [fecha, setFecha] = useState('');
  const [numOperacion, setNumOperacion] = useState('');
  const [remitente, setRemitente] = useState('');
  const [banco, setBanco] = useState<BancoCodigo | ''>('');
  const [motivo, setMotivo] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const montoNum = Number(monto.replace(/[^\d]/g, '')) || 0;
  const disabled =
    submitting ||
    montoNum <= 0 ||
    fecha === '' ||
    numOperacion.trim() === '' ||
    remitente.trim() === '';

  async function handleSubmit() {
    setSubmitting(true);
    setError(null);
    try {
      const pago = await registrarPagoManual({
        monto_total: montoNum,
        fecha_transferencia: fecha,
        num_operacion: numOperacion.trim(),
        remitente_nombre: remitente.trim(),
        banco: banco || null,
        motivo: motivo.trim() || null,
      });
      onSuccess(pago);
      onClose();
    } catch (e) {
      setError(mensajeError(e));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-pago-manual-titulo"
    >
      <div
        className="bg-bg-card border border-white/[0.08] rounded-lg max-w-md w-full p-5 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2
              id="modal-pago-manual-titulo"
              className="text-lg font-medium text-text-primary"
            >
              Registrar pago manual
            </h2>
            <p className="text-xs text-text-muted mt-1">
              Para un pago que no llegó por correo. Ingresa los datos del
              comprobante; luego lo clasificas a la casa con "Clasificar →".
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-text-muted hover:text-text-primary p-1 -mr-1"
            aria-label="Cerrar"
          >
            ×
          </button>
        </div>

        {/* Monto */}
        <label
          htmlFor="pago-manual-monto"
          className="block text-xs text-text-muted mb-1"
        >
          Monto <span className="text-state-danger">*</span>
        </label>
        <input
          id="pago-manual-monto"
          type="number"
          inputMode="numeric"
          value={monto}
          onChange={(e) => setMonto(e.target.value)}
          placeholder="0"
          autoFocus
          className="w-full bg-bg-sunken border border-white/[0.08] rounded px-3 py-2 text-text-primary text-base focus:outline-none focus:border-orange"
          style={{ fontVariantNumeric: 'tabular-nums' }}
        />

        {/* Fecha de transferencia */}
        <label
          htmlFor="pago-manual-fecha"
          className="block text-xs text-text-muted mt-4 mb-1"
        >
          Fecha de transferencia <span className="text-state-danger">*</span>
        </label>
        <InputFecha
          id="pago-manual-fecha"
          value={fecha}
          onChange={setFecha}
          max={hoy()}
          requerido
          aria-label="Fecha de transferencia"
        />
        <p className="text-[11px] text-text-muted mt-1 leading-relaxed">
          Usa la fecha real del comprobante. Si registraste un saldo del banco
          más reciente, esta fecha debe ser anterior o igual a ese cuadre, o el
          saldo calculado contaría el pago dos veces.
        </p>

        {/* N° de operación */}
        <label
          htmlFor="pago-manual-operacion"
          className="block text-xs text-text-muted mt-4 mb-1"
        >
          N° de operación <span className="text-state-danger">*</span>
        </label>
        <input
          id="pago-manual-operacion"
          type="text"
          value={numOperacion}
          onChange={(e) => setNumOperacion(e.target.value)}
          placeholder="Del comprobante"
          maxLength={50}
          className="w-full bg-bg-sunken border border-white/[0.08] rounded px-3 py-2 text-text-primary text-sm focus:outline-none focus:border-orange"
        />

        {/* Quién pagó */}
        <label
          htmlFor="pago-manual-remitente"
          className="block text-xs text-text-muted mt-4 mb-1"
        >
          Quién pagó <span className="text-state-danger">*</span>
        </label>
        <input
          id="pago-manual-remitente"
          type="text"
          value={remitente}
          onChange={(e) => setRemitente(e.target.value)}
          placeholder="Nombre del remitente"
          maxLength={255}
          className="w-full bg-bg-sunken border border-white/[0.08] rounded px-3 py-2 text-text-primary text-sm focus:outline-none focus:border-orange"
        />

        {/* Banco (opcional) */}
        <label
          htmlFor="pago-manual-banco"
          className="block text-xs text-text-muted mt-4 mb-1"
        >
          Banco{' '}
          <span className="text-text-muted">
            — opcional, para los reportes por banco
          </span>
        </label>
        <select
          id="pago-manual-banco"
          value={banco}
          onChange={(e) => setBanco(e.target.value as BancoCodigo | '')}
          className="w-full bg-bg-sunken border border-white/[0.08] rounded px-3 py-2 text-text-primary text-sm focus:outline-none focus:border-orange"
        >
          <option value="">Sin especificar</option>
          {BANCOS_CODIGOS.map((codigo) => (
            <option key={codigo} value={codigo}>
              {BANCOS_LABEL[codigo]}
            </option>
          ))}
        </select>

        {/* Motivo (opcional) */}
        <label
          htmlFor="pago-manual-motivo"
          className="block text-xs text-text-muted mt-4 mb-1"
        >
          Motivo <span className="text-text-muted">— opcional</span>
        </label>
        <textarea
          id="pago-manual-motivo"
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          rows={2}
          maxLength={500}
          placeholder="Ej: aviso por WhatsApp, comprobante adjunto"
          className="w-full bg-bg-sunken border border-white/[0.08] rounded px-3 py-2 text-text-primary text-sm focus:outline-none focus:border-orange resize-none"
        />

        {error && (
          <div className="mt-3 text-xs text-red-300 bg-red-500/10 border border-red-500/30 rounded p-2">
            {error}
          </div>
        )}

        <div className="flex gap-2 mt-5">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="flex-1 px-3 py-2 text-sm text-text-secondary border border-white/[0.08] rounded hover:text-text-primary hover:border-white/[0.16]"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={disabled}
            className="flex-1 px-3 py-2 text-sm font-medium bg-orange text-white rounded hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {submitting ? 'Registrando…' : 'Registrar pago'}
          </button>
        </div>
      </div>
    </div>
  );
}
